import { Injectable } from '@angular/core';
import { TimeService } from '@services/time-service';
import { AppConfig } from '../../app/app.config';

@Injectable({
  providedIn: 'root',
})
export class CrossSellRegimenService {
  constructor(
    private readonly timeService: TimeService,
    private readonly appConfig: AppConfig
  ) {}

  evaluateCrossSellProps(
    regimens: any[],
    experiment: any,
    isPaymentPage: boolean = false,
    checkoutOrder?: any,
    targetClass?: string,
  ): { showBanner: boolean, discount: number, deadline: Date } {
    if (!experiment) {
      return { showBanner: false, discount: 0, deadline: new Date() };
    }

    if (!isPaymentPage && this.isRegimenKitReadyBannerVisible(regimens)) {
      return { showBanner: false, discount: 0, deadline: new Date() };
    }

    if (this.isAnyRegimenPreparationTimePending(regimens)) {
      return { showBanner: false, discount: 0, deadline: new Date() };
    }

    const latestRegimen = regimens.find(regimen => regimen.active && regimen.delivered);
    if (!latestRegimen) {
      return { showBanner: false, discount: 0, deadline: new Date() };
    }

    const derivedTargetClass = targetClass || this.getTargetClassFromRegimen(latestRegimen);

    if (this.hasAlreadyDeliveredOrOrdered(derivedTargetClass, regimens)) {
      return { showBanner: false, discount: 0, deadline: new Date() };
    }

    const config = experiment.variant?.[derivedTargetClass];
    if (!config || this.isZeroDuration(config)) {
      return { showBanner: false, discount: 0, deadline: new Date() };
    }

    const deliveredOn = new Date(latestRegimen.activeFromDate.iso);
    const start = this.timeService.addDays(deliveredOn, Number(config.start_time));
    const end = this.timeService.addDays(deliveredOn, Number(config.end_time));
    const now = this.timeService.getEndOfDay();

    if (now < start || now > end) {
      return { showBanner: false, discount: 0, deadline: new Date() };
    }

    const orderType = typeof checkoutOrder?.get === 'function' ? checkoutOrder.get('type') : checkoutOrder?.type;
    if (isPaymentPage) {
      if (!orderType || orderType !== this.appConfig.Shared.Order.Type.REGIMEN || experiment?.variant.paymentPageDiscountDisclaimer === 'false') {
        return { showBanner: false, discount: 0, deadline: new Date() };
      }
    }

    return {
      showBanner: true,
      discount: config.discount,
      deadline: end,
    };
  }

  private getTargetClassFromRegimen(latestRegimen: any): string {
    return latestRegimen.class === this.appConfig.Shared.Regimen.Class.HAIR
      ? this.appConfig.Shared.Regimen.Class.FACE
      : this.appConfig.Shared.Regimen.Class.HAIR;
  }

  private hasAlreadyDeliveredOrOrdered(targetClass: string, regimens: any[]): boolean {
    return !!regimens?.some((regimen: any) =>
      regimen.class === targetClass && (regimen.delivered || regimen.orderPlaced)
    );
  }

  private isAnyRegimenPreparationTimePending(regimens: any[]): boolean {
    return !!regimens?.some((regimen: any) => regimen.isPreparationTimePending === true)
  }

  private isZeroDuration(config: { start_time: string; end_time: string }): boolean {
    return Number(config.start_time) === 0 && Number(config.end_time) === 0;
  }

  private isRegimenKitReadyBannerVisible(regimens: any[]): boolean {
    return regimens.some((regimen: any) =>
      !regimen.isPreparationTimePending &&
      !regimen.active &&
      !regimen.orderPlaced &&
      regimen.class !== this.appConfig.Shared.Regimen.Class.BODY
    );
  }
}
